/**
 * Sentinel for "create a new empty roster" — the user made a choice, but the
 * choice adds nothing to the payload. Kept distinct from `null` (no choice
 * yet) so the submit button can require an explicit decision, exactly as the
 * legacy roster step did.
 */
export const NEW_ROSTER = 'NEW'

export type RosterChoice = string | typeof NEW_ROSTER | null

export interface AddExistingTeamPayload {
  team_id: string
  duplicate_roster?: true
  source_team_player_roster_id?: string
}

export interface CreateTeamPayload {
  name: string
}

/**
 * Body for `POST /schedules/:divisionId/teams` when attaching a team that
 * already exists.
 *
 * Cloning is the ONLY case that adds fields. "New empty roster" and "a roster
 * for this competition already exists" both send the bare `team_id` — the
 * backend creates the roster implicitly. This mirrors the legacy Setup tab,
 * where the `'NEW'` sentinel likewise contributed nothing to the request.
 */
export const toAddExistingTeamPayload = (
  teamId: string,
  rosterChoice: RosterChoice
): AddExistingTeamPayload => {
  if (!rosterChoice || rosterChoice === NEW_ROSTER) {
    return { team_id: teamId }
  }

  return {
    team_id: teamId,
    duplicate_roster: true,
    source_team_player_roster_id: rosterChoice,
  }
}

/** Body for the same endpoint when creating a brand-new team by name. */
export const toCreateTeamPayload = (name: string): CreateTeamPayload => ({
  name: name.trim(),
})

/**
 * Whether the roster step can be submitted.
 *
 * A placeholder team has no roster to clone, and a team already holding a
 * roster for this competition needs no choice — both skip straight to enabled.
 * Everyone else must pick something, so a team is never added with an
 * accidentally empty roster.
 */
export const canSubmitRosterChoice = ({
  isPlaceholder,
  hasExistingCompetitionRoster,
  rosterChoice,
}: {
  isPlaceholder: boolean
  hasExistingCompetitionRoster: boolean
  rosterChoice: RosterChoice
}): boolean => {
  if (isPlaceholder || hasExistingCompetitionRoster) {
    return true
  }
  return rosterChoice !== null
}
